import React from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import './PrivacyPage.css';

const PrivacyPage: React.FC = () => {
  usePageTitle('Privacy Policy');
  const lastUpdated = "December 29, 2024";

  return (
    <div className="privacy-page">
      <section className="privacy-hero">
        <div className="container">
          <span className="section-label">Legal</span>
          <h1>Privacy Policy</h1>
          <p className="privacy-subtitle">
            How JuryView collects, uses, and protects your information
          </p>
          <p className="last-updated">Last Updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="privacy-content">
        <div className="container">
          <div className="policy-wrapper">
            <nav className="policy-nav">
              <h4>Contents</h4>
              <ul>
                <li><a href="#introduction">Introduction</a></li>
                <li><a href="#information-collected">Information We Collect</a></li>
                <li><a href="#how-we-use">How We Use Information</a></li>
                <li><a href="#data-sharing">Data Sharing</a></li>
                <li><a href="#data-retention">Data Retention</a></li>
                <li><a href="#security">Security Measures</a></li>
                <li><a href="#your-rights">Your Rights</a></li>
                <li><a href="#cookies">Cookies & Tracking</a></li>
                <li><a href="#third-party">Third-Party Services</a></li>
                <li><a href="#international">International Transfers</a></li>
                <li><a href="#changes">Policy Changes</a></li>
                <li><a href="#contact">Contact Us</a></li>
              </ul>
            </nav>

            <div className="policy-body">
              <section id="introduction" className="policy-section">
                <h2>1. Introduction</h2>
                <p>
                  JuryView, Inc. ("JuryView," "we," "us," or "our") is committed to protecting the privacy
                  and security of our clients, users, and website visitors. This Privacy Policy describes
                  how we collect, use, disclose, and safeguard information when you use our synthetic juror
                  technology platform, visit our website, or engage with our services.
                </p>
                <p>
                  By accessing or using JuryView's services, you acknowledge that you have read, understood,
                  and agree to be bound by this Privacy Policy. If you do not agree with our policies and
                  practices, please do not use our services.
                </p>
                <p>
                  JuryView provides AI-powered jury simulation and litigation analytics services to law firms,
                  trial consultants, insurance carriers, and legal technology providers. We understand the
                  sensitive nature of legal matters and are committed to maintaining the highest standards
                  of data protection and confidentiality.
                </p>
              </section>

              <section id="information-collected" className="policy-section">
                <h2>2. Information We Collect</h2>

                <h3>2.1 Information You Provide Directly</h3>
                <ul>
                  <li><strong>Account Information:</strong> Name, email address, phone number, job title, company name, and professional credentials when you register for our services or request information.</li>
                  <li><strong>Case Data:</strong> Case materials, evidence documents, party information, and venue details that you upload or input into our platform for analysis.</li>
                  <li><strong>Communication Data:</strong> Information you provide when contacting our support team, participating in surveys, or providing feedback.</li>
                  <li><strong>Payment Information:</strong> Billing address and payment details processed through our secure payment processors.</li>
                </ul>

                <h3>2.2 Information Collected Automatically</h3>
                <ul>
                  <li><strong>Usage Data:</strong> Information about how you interact with our platform, including features used, analyses run, and time spent on various functions.</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and IP address.</li>
                  <li><strong>Log Data:</strong> Server logs recording access times, pages viewed, and referring URLs.</li>
                </ul>

                <h3>2.3 Information from Third Parties</h3>
                <ul>
                  <li><strong>Public Records:</strong> Publicly available court records, judicial opinions, and legal databases used to calibrate our analytical models.</li>
                  <li><strong>Partner Data:</strong> Information received from integration partners when you connect JuryView with other legal technology platforms.</li>
                </ul>
              </section>

              <section id="how-we-use" className="policy-section">
                <h2>3. How We Use Your Information</h2>
                <p>We use the information we collect for the following purposes:</p>

                <h3>3.1 Service Delivery</h3>
                <ul>
                  <li>Providing synthetic juror simulations and jury panel analysis</li>
                  <li>Generating verdict predictions and case assessments</li>
                  <li>Delivering judge analytics and judicial behavior insights</li>
                  <li>Processing and responding to your requests and inquiries</li>
                </ul>

                <h3>3.2 Platform Improvement</h3>
                <ul>
                  <li>Enhancing the accuracy of our predictive models</li>
                  <li>Developing new features and analytical capabilities</li>
                  <li>Conducting research to improve venue-specific calibration</li>
                  <li>Analyzing usage patterns to optimize user experience</li>
                </ul>

                <h3>3.3 Communication</h3>
                <ul>
                  <li>Sending service-related notifications and updates</li>
                  <li>Providing technical support and customer service</li>
                  <li>Sharing relevant product announcements (with your consent)</li>
                </ul>

                <h3>3.4 Legal and Compliance</h3>
                <ul>
                  <li>Complying with applicable laws and regulations</li>
                  <li>Enforcing our terms of service and agreements</li>
                  <li>Protecting our rights and the rights of our users</li>
                </ul>
              </section>

              <section id="data-sharing" className="policy-section">
                <h2>4. Data Sharing and Disclosure</h2>
                <p>
                  We do not sell, rent, or trade your personal information or case data. We may share
                  information only in the following limited circumstances:
                </p>

                <h3>4.1 Service Providers</h3>
                <p>
                  We work with carefully selected third-party service providers who assist in operating
                  our platform, including cloud hosting providers, payment processors, and analytics services.
                  These providers are contractually bound to protect your information and may only use it
                  for the specific services they provide to us.
                </p>

                <h3>4.2 Legal Requirements</h3>
                <p>
                  We may disclose information when required by law, court order, or governmental authority,
                  or when we believe in good faith that disclosure is necessary to protect our rights,
                  your safety, or the safety of others.
                </p>

                <h3>4.3 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be
                  transferred as part of the transaction. We will notify you of any such change and
                  the choices you may have regarding your information.
                </p>

                <h3>4.4 With Your Consent</h3>
                <p>
                  We may share information with third parties when you explicitly consent to such sharing.
                </p>
              </section>

              <section id="data-retention" className="policy-section">
                <h2>5. Data Retention</h2>
                <p>
                  We retain your information for as long as necessary to fulfill the purposes outlined
                  in this Privacy Policy, unless a longer retention period is required or permitted by law.
                </p>
                <ul>
                  <li><strong>Account Data:</strong> Retained for the duration of your account plus 3 years for compliance purposes.</li>
                  <li><strong>Case Data:</strong> Retained according to your subscription terms, with options for immediate deletion upon request.</li>
                  <li><strong>Usage Logs:</strong> Retained for up to 2 years for security and analytics purposes.</li>
                  <li><strong>Aggregated Data:</strong> De-identified, aggregated data may be retained indefinitely for research and model improvement.</li>
                </ul>
              </section>

              <section id="security" className="policy-section">
                <h2>6. Security Measures</h2>
                <p>
                  We implement comprehensive technical and organizational measures to protect your
                  information. For detailed information about our security practices, please see our
                  <Link to="/security"> Security Policy</Link>.
                </p>
                <p>Key security measures include:</p>
                <ul>
                  <li>AES-256 encryption for data at rest</li>
                  <li>TLS 1.3 encryption for data in transit</li>
                  <li>SOC 2 Type II certified infrastructure</li>
                  <li>Regular security audits and penetration testing</li>
                  <li>Role-based access controls and multi-factor authentication</li>
                  <li>Continuous security monitoring and incident response</li>
                </ul>
              </section>

              <section id="your-rights" className="policy-section">
                <h2>7. Your Rights and Choices</h2>
                <p>Depending on your location, you may have the following rights regarding your personal information:</p>

                <h3>7.1 Access and Portability</h3>
                <p>
                  You may request a copy of the personal information we hold about you in a structured,
                  commonly used, and machine-readable format.
                </p>

                <h3>7.2 Correction</h3>
                <p>
                  You may request that we correct inaccurate or incomplete personal information.
                </p>

                <h3>7.3 Deletion</h3>
                <p>
                  You may request deletion of your personal information, subject to certain legal
                  exceptions and retention requirements.
                </p>

                <h3>7.4 Restriction and Objection</h3>
                <p>
                  You may request that we restrict processing of your information or object to certain
                  processing activities.
                </p>

                <h3>7.5 California Residents</h3>
                <p>
                  California residents have additional rights under the California Consumer Privacy Act
                  (CCPA), including the right to know what personal information is collected, the right
                  to delete, and the right to opt-out of the sale of personal information. We do not
                  sell personal information.
                </p>

                <h3>7.6 European Residents</h3>
                <p>
                  If you are located in the European Economic Area (EEA), you have rights under the
                  General Data Protection Regulation (GDPR), including the rights described above.
                  Our legal basis for processing depends on the specific context and purposes.
                </p>

                <p>
                  To exercise any of these rights, please contact us at{' '}
                  <a href="mailto:privacy@juryview.com">privacy@juryview.com</a>.
                </p>
              </section>

              <section id="cookies" className="policy-section">
                <h2>8. Cookies and Tracking Technologies</h2>
                <p>We use cookies and similar technologies to:</p>
                <ul>
                  <li>Maintain your session and authentication status</li>
                  <li>Remember your preferences and settings</li>
                  <li>Analyze website traffic and usage patterns</li>
                  <li>Improve our services and user experience</li>
                </ul>

                <h3>Types of Cookies We Use</h3>
                <ul>
                  <li><strong>Essential Cookies:</strong> Required for basic platform functionality and security.</li>
                  <li><strong>Functional Cookies:</strong> Enable personalization and enhanced features.</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform.</li>
                </ul>

                <p>
                  You can control cookies through your browser settings. However, disabling certain
                  cookies may limit your ability to use some features of our platform.
                </p>
              </section>

              <section id="third-party" className="policy-section">
                <h2>9. Third-Party Services</h2>
                <p>
                  Our platform may contain links to third-party websites or integrate with third-party
                  services. This Privacy Policy does not apply to those third parties, and we encourage
                  you to review their privacy policies before providing any information.
                </p>
                <p>Third-party services we may integrate with include:</p>
                <ul>
                  <li>Cloud infrastructure providers (AWS, Google Cloud)</li>
                  <li>Payment processors (Stripe)</li>
                  <li>Analytics services (privacy-focused analytics only)</li>
                  <li>Customer support platforms</li>
                </ul>
              </section>

              <section id="international" className="policy-section">
                <h2>10. International Data Transfers</h2>
                <p>
                  JuryView is based in the United States. If you access our services from outside the
                  United States, please be aware that your information may be transferred to, stored,
                  and processed in the United States or other countries where our service providers
                  are located.
                </p>
                <p>
                  We take appropriate safeguards to ensure that your personal information remains
                  protected in accordance with this Privacy Policy, including implementing Standard
                  Contractual Clauses approved by the European Commission for transfers from the EEA.
                </p>
              </section>

              <section id="changes" className="policy-section">
                <h2>11. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our
                  practices, technology, legal requirements, or other factors. We will notify you
                  of any material changes by posting the updated policy on our website and updating
                  the "Last Updated" date.
                </p>
                <p>
                  For significant changes, we will provide additional notice, such as an email
                  notification to registered users. We encourage you to review this Privacy Policy
                  periodically.
                </p>
              </section>

              <section id="contact" className="policy-section">
                <h2>12. Contact Us</h2>
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or our
                  data practices, please contact us:
                </p>
                <div className="contact-info">
                  <p><strong>JuryView, Inc.</strong></p>
                  <p>Privacy Inquiries</p>
                  <p>Email: <a href="mailto:privacy@juryview.com">privacy@juryview.com</a></p>
                </div>
                <p>
                  For data protection inquiries from the European Union, you may also contact our
                  EU representative or lodge a complaint with your local supervisory authority.
                </p>
              </section>

              <div className="policy-footer">
                <p>
                  This Privacy Policy is part of and incorporated into JuryView's Terms of Service.
                  By using our services, you also agree to our <Link to="/security">Security Policy</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
